import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, User, Users, Search, ArrowUpDown, ArrowUp, ArrowDown, Facebook, Linkedin, Instagram } from 'lucide-react';
import { OptimizedImage } from '../ui/optimized-image';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MemberContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'last_name', direction: 'asc' });
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/member/contacts`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleImageError = (contactId) => {
    setImageErrors(prev => ({ ...prev, [contactId]: true }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1 inline" />
      : <ArrowDown className="w-4 h-4 ml-1 inline" />;
  };

  // Filtrer les contacts
  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contact.first_name?.toLowerCase().includes(searchLower) ||
      contact.last_name?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.phone?.toLowerCase().includes(searchLower) ||
      contact.city?.toLowerCase().includes(searchLower) ||
      contact.address?.toLowerCase().includes(searchLower)
    );
  });

  // Trier les contacts
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    let aValue = a[sortConfig.key] || '';
    let bValue = b[sortConfig.key] || '';
    
    // Gestion spéciale pour le nom complet
    if (sortConfig.key === 'name') {
      aValue = `${a.last_name} ${a.first_name}`.toLowerCase();
      bValue = `${b.last_name} ${b.first_name}`.toLowerCase();
    } else {
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Lora, serif', color: 'rgb(196, 150, 96)' }}>
            Contacts des Membres
          </h1>
          <p className="text-gray-600" style={{ fontFamily: 'Lora, serif' }}>
            Coordonnées des membres qui ont accepté de partager leurs informations
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone, ville..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ fontFamily: 'Lora, serif' }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'Lora, serif' }}>
            {sortedContacts.length} membre{sortedContacts.length > 1 ? 's' : ''} {searchTerm && `trouvé${sortedContacts.length > 1 ? 's' : ''}`}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Lora, serif' }}
                  >
                    Nom et Prénom
                    {getSortIcon('name')}
                  </th>
                  <th
                    onClick={() => handleSort('email')}
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Lora, serif' }}
                  >
                    Adresse email
                    {getSortIcon('email')}
                  </th>
                  <th
                    onClick={() => handleSort('phone')}
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Lora, serif' }}
                  >
                    Téléphone
                    {getSortIcon('phone')}
                  </th>
                  <th
                    onClick={() => handleSort('city')}
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    style={{ fontFamily: 'Lora, serif' }}
                  >
                    Adresse postale
                    {getSortIcon('city')}
                  </th>
                  <th
                    className="px-6 py-4 text-left text-sm font-semibold text-gray-700"
                    style={{ fontFamily: 'Lora, serif' }}
                  >
                    Réseaux sociaux
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedContacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                    {/* Nom et Prénom */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {contact.profile_picture && !imageErrors[contact.id] ? (
                          <OptimizedImage
                            src={`${API_BASE}${contact.profile_picture}`}
                            alt={`${contact.first_name} ${contact.last_name}`}
                            className="w-10 h-10 rounded-full object-cover mr-3"
                            useThumbnail={true}
                            onError={() => handleImageError(contact.id)}
                          />
                        ) : (
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                            style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                          >
                            <User className="w-6 h-6 text-white" />
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Lora, serif' }}>
                            {contact.first_name} {contact.last_name}
                          </div>
                          {contact.lignage && (
                            <div className="text-xs text-gray-500 max-w-[200px]" style={{ fontFamily: 'Lora, serif' }}>
                              {(() => {
                                // Gérer le cas où lignage est un tableau ou une chaîne
                                let lignages = contact.lignage;
                                if (typeof lignages === 'string') {
                                  // Si c'est une chaîne, essayer de la parser ou la splitter
                                  lignages = lignages.split(',').map(l => l.trim()).filter(l => l);
                                }
                                if (Array.isArray(lignages) && lignages.length > 0) {
                                  // Grouper par 3 avec retour à la ligne
                                  const groups = [];
                                  for (let i = 0; i < lignages.length; i += 3) {
                                    groups.push(lignages.slice(i, i + 3).join(' • '));
                                  }
                                  return groups.map((group, idx) => (
                                    <div key={idx}>{group}</div>
                                  ));
                                }
                                return lignages;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-6 py-4">
                      {contact.email ? (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-sm text-blue-600 hover:underline flex items-center"
                          style={{ fontFamily: 'Lora, serif' }}
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          {contact.email}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400 italic" style={{ fontFamily: 'Lora, serif' }}>
                          Non partagé
                        </span>
                      )}
                    </td>

                    {/* Téléphone */}
                    <td className="px-6 py-4">
                      {(contact.phone || contact.gsm) ? (
                        <div className="text-sm" style={{ fontFamily: 'Lora, serif' }}>
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline flex items-center mb-1">
                              <Phone className="w-4 h-4 mr-2" />
                              {contact.phone}
                            </a>
                          )}
                          {contact.gsm && (
                            <a href={`tel:${contact.gsm}`} className="text-blue-600 hover:underline flex items-center">
                              <Phone className="w-4 h-4 mr-2" />
                              {contact.gsm} <span className="text-xs text-gray-500 ml-1">(GSM)</span>
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic" style={{ fontFamily: 'Lora, serif' }}>
                          Non partagé
                        </span>
                      )}
                    </td>

                    {/* Adresse */}
                    <td className="px-6 py-4">
                      {contact.address ? (
                        <div className="text-sm text-gray-700" style={{ fontFamily: 'Lora, serif' }}>
                          <div className="flex items-start">
                            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                              <div>{contact.address}</div>
                              <div>{contact.postal_code} {contact.city}</div>
                              {contact.country && <div className="text-xs text-gray-500">{contact.country}</div>}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic" style={{ fontFamily: 'Lora, serif' }}>
                          Non partagée
                        </span>
                      )}
                    </td>

                    {/* Réseaux sociaux */}
                    <td className="px-6 py-4">
                      {(contact.facebook || contact.linkedin || contact.instagram) ? (
                        <div className="flex gap-2">
                          {contact.facebook && (
                            <a 
                              href={contact.facebook} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Facebook"
                            >
                              <Facebook className="w-5 h-5" />
                            </a>
                          )}
                          {contact.linkedin && (
                            <a 
                              href={contact.linkedin} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-blue-700 hover:text-blue-900 transition-colors"
                              title="LinkedIn"
                            >
                              <Linkedin className="w-5 h-5" />
                            </a>
                          )}
                          {contact.instagram && (
                            <a 
                              href={contact.instagram} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-pink-600 hover:text-pink-800 transition-colors"
                              title="Instagram"
                            >
                              <Instagram className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic" style={{ fontFamily: 'Lora, serif' }}>
                          Non partagés
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {sortedContacts.length === 0 && !loading && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500" style={{ fontFamily: 'Lora, serif' }}>
              {searchTerm 
                ? "Aucun membre trouvé avec ce critère de recherche"
                : "Aucun membre n'a partagé ses coordonnées pour le moment"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberContacts;
